import { useRef, useLayoutEffect, useState, useMemo, useCallback } from "react";
import { SxProps, Theme } from "@mui/material";

// Simple debounce utility
function debounce<T extends (...args: any[]) => void>(fn: T, ms: number): T {
  let timer: ReturnType<typeof setTimeout>;
  return ((...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as T;
}

export const useStickyColumns = (
  leftSticky: number[],
  rightSticky: number[],
) => {
  const headerRefs = useRef<Array<HTMLTableCellElement | null>>([]);
  const [leftOffsets, setLeftOffsets] = useState<Record<number, number>>({});
  const [rightOffsets, setRightOffsets] = useState<Record<number, number>>({});

  const leftSet = useMemo(() => new Set(leftSticky), [leftSticky]);
  const rightSet = useMemo(() => new Set(rightSticky), [rightSticky]);

  const computeOffsets = useCallback(() => {
    const refs = headerRefs.current;
    if (!refs.length) return;

    // offsetWidth does NOT trigger reflow if layout is already clean
    const widths = refs.map((el) => el?.offsetWidth ?? 0);

    const newLeft: Record<number, number> = {};
    let cumLeft = 0;
    widths.forEach((width, index) => {
      if (leftSet.has(index)) {
        newLeft[index] = cumLeft;
        cumLeft += width;
      }
    });

    const newRight: Record<number, number> = {};
    let cumRight = 0;
    [...rightSet]
      .sort((a, b) => b - a)
      .forEach((index) => {
        newRight[index] = cumRight;
        cumRight += widths[index];
      });

    setLeftOffsets((prev) => {
      for (const k in newLeft) {
        if (newLeft[k] !== prev[+k]) return newLeft;
      }
      return prev;
    });

    setRightOffsets((prev) => {
      for (const k in newRight) {
        if (newRight[k] !== prev[+k]) return newRight;
      }
      return prev;
    });
  }, [leftSet, rightSet]);

  // Debounced version for ResizeObserver (fires rapidly during resize)
  const debouncedCompute = useMemo(
    () => debounce(computeOffsets, 50),
    [computeOffsets],
  );

  useLayoutEffect(() => {
    // Run once immediately (no debounce for initial render)
    computeOffsets();

    const resizeObserver = new ResizeObserver(debouncedCompute);
    headerRefs.current.forEach((el) => {
      if (el) resizeObserver.observe(el);
    });

    return () => resizeObserver.disconnect();
  }, [computeOffsets, debouncedCompute]);

  const setHeaderRef = (index: number) => (el: Element | null) => {
    headerRefs.current[index] = el as HTMLTableCellElement | null;
  };

  const getHeaderSx = (index: number): SxProps<Theme> => {
    const base: any = {};

    if (leftSet.has(index)) {
      base.position = "sticky";
      base.left = leftOffsets[index] ?? 0;
      base.zIndex = 10;
      base.backgroundColor = "background.paper";

      if (index === Math.max(...leftSet)) {
        base.borderRight = "1px solid #e0e0e0";
      }
    }

    if (rightSet.has(index)) {
      base.position = "sticky";
      base.right = rightOffsets[index] ?? 0;
      base.zIndex = 9;
      base.backgroundColor = "background.paper";

      if (index === Math.max(...rightSet)) {
        base.borderLeft = "1px solid #e0e0e0";
      }
    }

    return base;
  };

  const getBodySx = (index: number, isSelected?: boolean): SxProps<Theme> => {
    const base: any = {};

    if (leftSet.has(index)) {
      base.position = "sticky";
      base.left = leftOffsets[index] ?? 0;
      base.zIndex = 2;
      base.backgroundColor = isSelected ? "#f0def3" : "background.paper";

      if (index === Math.max(...leftSet)) {
        base.borderRight = "1px solid #e0e0e0";
      }
    }

    if (rightSet.has(index)) {
      base.position = "sticky";
      base.right = rightOffsets[index] ?? 0;
      base.zIndex = 2;
      base.backgroundColor = isSelected ? "#f0def3" : "background.paper";

      if (index === Math.max(...rightSet)) {
        base.borderLeft = "1px solid #e0e0e0";
      }
    }

    return base;
  };

  return { setHeaderRef, getHeaderSx, getBodySx };
};
